import moment from "moment";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { guardarExamenInterconsultaService } from "../../../Service/Service.examen";
import { ListarEspecialidadClinicaActivas } from "../../../Service/Service.especialidad";
import { CITA_FINALIZADA_SENTINEL } from "../../../utils/citaFinalizadaAlert";
import { GetTokenDecoded } from "../../../utils/storeUsuario";
import { hcError, hcExito } from "../hcSwal";
import HCModal from "../HCModal";
import { Autocomplete, DateBadge, Field, OrderBadge, Section, Segmented } from "../HCPiezas";
import TabDiagnosticos from "./TabDiagnosticos";

/* ============================================================================
   Orden de interconsulta — migrado a HCModal.

   Payload y endpoint idénticos: POST /examen_registro_cita_interconsulta_orden.
   `resumen_examenes` y `planes_terapeuticos` se quedan como campos de texto,
   tal como pidió el cliente: son columnas de prosa y los registros históricos
   son prosa.

   Un cambio que sí hago: la especialidad venía de una lista escrita a mano en
   la vista con TRES opciones (Medicina General, Ginecología, Traumatología),
   mientras el sistema tiene su tabla de especialidades. Ahora se lee del
   catálogo real y ADEMÁS se permite texto libre, así que nada de lo que se
   podía escribir antes deja de poderse.
   ========================================================================== */

const PRIORIDADES = [
  { value: "rutina", label: "Rutina" },
  { value: "control", label: "Control" },
  { value: "urgente", label: "Urgente", peligro: true },
];

const FORM_VACIO = {
  idcita: 0,
  IdCategoriaExamen: 0,
  idpaciente: 0,
  fecha: moment().format("YYYY-MM-DD"),
  numero_orden: "",
  sala: "",
  motivo: "",
  resumen_clinico: "",
  especialidad: "",
  resumen_examenes: "",
  planes_terapeuticos: "",
  IdExamenTipo: [],
};

export default function ModalInterconsulta({
  show,
  onHide,
  onSaveSuccess,
  cita,
  paciente,
  IdCategoriaExamen,
  ListaCitaPaciente,
}) {
  const userLocal = GetTokenDecoded();
  const [tab, setTab] = useState("orden");
  const [form, setForm] = useState(FORM_VACIO);
  const [especialidades, setEspecialidades] = useState([]);
  const [prioridad, setPrioridad] = useState("rutina"); // no persiste: no hay columna
  const [loading, setLoading] = useState(false);
  const [intentado, setIntentado] = useState(false);
  const espRef = useRef(null);
  const motivoRef = useRef(null);

  useEffect(() => {
    if (!show || !cita) return;
    setForm({
      ...FORM_VACIO,
      idcita: cita.idcita,
      idpaciente: cita.pacientes?.idpaciente,
      IdCategoriaExamen,
      numero_orden: `INTER-${cita.idcita}-CECOLON`,
      fecha: moment().format("YYYY-MM-DD"),
    });
    setPrioridad("rutina");
    setIntentado(false);
    setTab("orden");
    (async () => {
      // El endpoint devuelve filas de `clinica_especialidades` con la
      // especialidad ANIDADA (`especialidades.especialidad`), no plana.
      const r = await ListarEspecialidadClinicaActivas(userLocal?.idclinica);
      const nombres = (r?.data || [])
        .map((e) => e?.especialidades?.especialidad ?? e?.especialidad)
        .filter(Boolean);
      // Sin duplicados y en orden: una clínica puede repetir especialidad.
      const unicos = [...new Set(nombres)].sort((a, b) => a.localeCompare(b, "es"));
      setEspecialidades(unicos.map((n) => ({ value: n, label: n })));
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, cita, IdCategoriaExamen]);

  const buscarEsp = (t) => {
    const q = t.toLowerCase();
    return especialidades.filter((e) => e.label.toLowerCase().includes(q)).slice(0, 30);
  };

  const espElegida = form.especialidad ? { value: form.especialidad, label: form.especialidad } : null;

  const faltantes = useMemo(() => {
    const f = [];
    if (!form.especialidad) f.push("Especialidad");
    if (!form.motivo.trim()) f.push("Motivo");
    if (!form.fecha) f.push("Fecha");
    return f;
  }, [form]);

  const enfermedadActual = cita?.enfermedad_actual_cita?.[0]?.enfermedad_actual || "";

  const guardar = async () => {
    setIntentado(true);
    if (faltantes.length > 0) {
      const destino = faltantes[0] === "Especialidad" ? espRef.current : motivoRef.current;
      destino?.scrollIntoView({ behavior: "smooth", block: "center" });
      destino?.focus?.({ preventScroll: true });
      return;
    }
    setLoading(true);
    const data = await guardarExamenInterconsultaService(form);
    if (data === CITA_FINALIZADA_SENTINEL) {
      setLoading(false);
      return;
    }
    if (!data || data.success === false) {
      hcError("No se pudo guardar", "Intenta de nuevo en unos segundos.");
      setLoading(false);
      return;
    }
    hcExito("Orden de interconsulta enviada", form.especialidad);
    setLoading(false);
    if (onSaveSuccess) await onSaveSuccess();
    cerrar();
  };

  const cerrar = () => {
    setForm(FORM_VACIO);
    onHide && onHide();
  };

  const diagnosticos = cita?.diagnostico_cita_paciente || [];
  const subtitulo = [
    paciente?.nombres_completos || cita?.pacientes?.nombres_completos,
    paciente?.edad ? `${paciente.edad} a` : null,
    cita?.idcita ? `Cita ${cita.idcita}` : null,
    [cita?.doctor?.prefijo, cita?.doctor?.nombre].filter(Boolean).join(" ") || null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <HCModal
      isOpen={!!show}
      size="lg"
      title="Orden de interconsulta"
      subtitle={subtitulo}
      meta={[
        <DateBadge key="f" value={moment(form.fecha).format("DD/MM/YYYY")} label="Fecha de la orden" />,
        <OrderBadge key="o" code={form.numero_orden} />,
      ]}
      tabs={[
        { key: "orden", label: "Orden" },
        { key: "diagnostico", label: "Diagnóstico", count: diagnosticos.length },
      ]}
      activeTab={tab}
      onTabChange={setTab}
      validation={{ missing: faltantes }}
      primaryLabel="Agregar orden"
      onPrimary={guardar}
      primaryLoading={loading}
      onClose={cerrar}
      onCancel={cerrar}
      hayCambiosSinGuardar={Boolean(form.especialidad || form.motivo || form.resumen_clinico)}
    >
      {tab === "orden" ? (
        <>
          <Section title="1 · Especialidad solicitada">
            <div className="hcgrid hcgrid--3">
              <Field
                label="Especialidad"
                required
                htmlFor="hc-ic-esp"
                error={intentado && !form.especialidad ? "Este campo es obligatorio" : null}
              >
                <div ref={espRef}>
                  <Autocomplete
                    id="hc-ic-esp"
                    valor={espElegida}
                    onElegir={(op) => setForm({ ...form, especialidad: op ? op.value : "" })}
                    onBuscar={buscarEsp}
                    permitirLibre
                    minimo={1}
                    placeholder="Escribe la especialidad…"
                    vacio="No está en el catálogo"
                    ariaInvalid={intentado && !form.especialidad ? "true" : undefined}
                  />
                </div>
              </Field>

              <Field label="Prioridad" hint="Referencia para el servicio; no se guarda todavía.">
                <Segmented ariaLabel="Prioridad" options={PRIORIDADES} value={prioridad} onChange={setPrioridad} />
              </Field>

              <Field label="Sala" htmlFor="hc-ic-sala">
                <input
                  id="hc-ic-sala"
                  type="text"
                  placeholder="Sin asignar"
                  value={form.sala}
                  onChange={(e) => setForm({ ...form, sala: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title="2 · Justificación clínica">
            <div className="hcgrid hcgrid--2">
              <Field
                label="Motivo de la solicitud"
                required
                htmlFor="hc-ic-motivo"
                error={intentado && !form.motivo.trim() ? "Este campo es obligatorio" : null}
              >
                <textarea
                  id="hc-ic-motivo"
                  ref={motivoRef}
                  rows={4}
                  placeholder="Requerido para enviar la orden"
                  aria-invalid={intentado && !form.motivo.trim() ? "true" : undefined}
                  value={form.motivo}
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                />
              </Field>
              <Field
                label="Resumen clínico"
                htmlFor="hc-ic-resumen"
                hint={
                  enfermedadActual ? (
                    <button
                      type="button"
                      className="hcbtn hcbtn--link"
                      style={{ minHeight: 0, padding: 0 }}
                      onClick={() => setForm({ ...form, resumen_clinico: enfermedadActual })}
                    >
                      Copiar de enfermedad actual
                    </button>
                  ) : null
                }
              >
                <textarea
                  id="hc-ic-resumen"
                  rows={4}
                  placeholder="Opcional"
                  value={form.resumen_clinico}
                  onChange={(e) => setForm({ ...form, resumen_clinico: e.target.value })}
                />
              </Field>
            </div>
          </Section>

          <Section title="3 · Exámenes y plan" hint="Campos de texto, como estaban">
            <div className="hcgrid hcgrid--2">
              <Field label="Resultado de exámenes" htmlFor="hc-ic-exam">
                <textarea
                  id="hc-ic-exam"
                  rows={4}
                  placeholder="Exámenes relevantes y sus resultados"
                  value={form.resumen_examenes}
                  onChange={(e) => setForm({ ...form, resumen_examenes: e.target.value })}
                />
              </Field>
              <Field label="Planes terapéuticos" htmlFor="hc-ic-planes">
                <textarea
                  id="hc-ic-planes"
                  rows={4}
                  placeholder="Tratamiento instaurado hasta ahora"
                  value={form.planes_terapeuticos}
                  onChange={(e) => setForm({ ...form, planes_terapeuticos: e.target.value })}
                />
              </Field>
            </div>
          </Section>
        </>
      ) : (
        <TabDiagnosticos diagnosticos={diagnosticos} onRefrescar={ListaCitaPaciente} />
      )}
    </HCModal>
  );
}
